import type {
  EligibleApplicantsResponse,
  EligibleMonitoringCheck,
  EnrollmentDetailCheck,
} from "./types";

interface CaseCheckTypeSummary {
  type: string;
  status: string;
}

/**
 * Subset of an enrollment needed to match it to a case. Kept narrower than the
 * full detail schema so callers (and tests) only need to supply matching
 * fields, not the richer detail-page fields.
 */
type EnrollmentForCaseMatching = Pick<
  EnrollmentDetailCheck,
  | "id"
  | "check_type"
  | "monitor_status"
  | "last_run_result"
  | "last_run_date"
  | "next_run_date"
  | "monitor_end_date"
  | "reference_case_short_id"
  | "initial_reference_case_short_id"
  | "reference_check_id"
>;

const MONITORING_ACTIVITY_STATUSES = new Set([
  "ACTIVE",
  "PAUSED",
  "DEACTIVATED",
]);

function enrollmentMatchesCase(
  enrollment: EnrollmentForCaseMatching,
  caseShortId: string,
  eligibleCheckTypes: Set<string>
): boolean {
  return (
    enrollment.initial_reference_case_short_id === caseShortId ||
    enrollment.reference_case_short_id === caseShortId ||
    eligibleCheckTypes.has(enrollment.check_type)
  );
}

function isMonitoringActivityStatus(monitorStatus: string): boolean {
  return MONITORING_ACTIVITY_STATUSES.has(monitorStatus);
}

/** All eligible monitoring checks for a subject. */
export function getEligibleChecksForSubject(
  eligibleData: EligibleApplicantsResponse | undefined,
  subjectId: string | undefined
): EligibleMonitoringCheck[] {
  if (!eligibleData?.results || !subjectId) {
    return [];
  }

  const applicant = eligibleData.results.find(
    (entry) => entry.id === subjectId
  );
  return [...(applicant?.eligible_monitoring_checks ?? [])];
}

/** Eligible monitoring checks for a subject on a specific case. */
export function getEligibleChecksForCase(
  eligibleData: EligibleApplicantsResponse | undefined,
  subjectId: string | undefined,
  caseShortId: string | undefined
): EligibleMonitoringCheck[] {
  if (!eligibleData?.results || !subjectId || !caseShortId) {
    return [];
  }

  const applicant = eligibleData.results.find(
    (entry) => entry.id === subjectId
  );
  if (!applicant) {
    return [];
  }

  return applicant.eligible_monitoring_checks.filter(
    (check) => check.case_short_id === caseShortId
  );
}

/**
 * Monitoring-relevant check types on a case. Uses eligible checks from the API
 * and, when the subject is already enrolled, includes active enrollment types
 * that exist as completed checks on the case (the eligible API omits enrolled
 * checks, but we still need to match after a case update).
 */
export function getMonitoringCheckTypesForCase(
  eligibleChecks: EligibleMonitoringCheck[],
  caseChecks: readonly CaseCheckTypeSummary[],
  enrollments: readonly EnrollmentForCaseMatching[] | undefined
): Set<string> {
  const types = new Set(eligibleChecks.map((check) => check.type));

  const completeCaseCheckTypes = new Set(
    caseChecks
      .filter((check) => check.status === "COMPLETE")
      .map((check) => check.type)
  );

  for (const enrollment of enrollments ?? []) {
    if (
      isMonitoringActivityStatus(enrollment.monitor_status) &&
      completeCaseCheckTypes.has(enrollment.check_type)
    ) {
      types.add(enrollment.check_type);
    }
  }

  return types;
}

export function hasActiveEnrollmentForCase(
  enrollments: readonly EnrollmentForCaseMatching[] | undefined,
  caseShortId: string,
  eligibleCheckTypes: Set<string>
): boolean {
  return (enrollments ?? []).some(
    (enrollment) =>
      enrollment.monitor_status === "ACTIVE" &&
      enrollmentMatchesCase(enrollment, caseShortId, eligibleCheckTypes)
  );
}

/** True when the case should link to the monitoring dashboard (active or on hold). */
export function hasMonitoringActivityEnrollmentForCase(
  enrollments: readonly EnrollmentForCaseMatching[] | undefined,
  caseShortId: string,
  eligibleCheckTypes: Set<string>
): boolean {
  return (enrollments ?? []).some(
    (enrollment) =>
      isMonitoringActivityStatus(enrollment.monitor_status) &&
      enrollmentMatchesCase(enrollment, caseShortId, eligibleCheckTypes)
  );
}

/** Dashboard URL with a monitor_status filter that includes this case's enrollments. */
export function getMonitoringDashboardUrlForCase(
  enrollments: readonly EnrollmentForCaseMatching[] | undefined,
  caseShortId: string,
  eligibleCheckTypes: Set<string>
): string {
  const matchingStatuses = new Set(
    (enrollments ?? [])
      .filter(
        (enrollment) =>
          isMonitoringActivityStatus(enrollment.monitor_status) &&
          enrollmentMatchesCase(enrollment, caseShortId, eligibleCheckTypes)
      )
      .map((enrollment) => enrollment.monitor_status)
  );

  if (matchingStatuses.size === 1) {
    const [status] = matchingStatuses;
    if (status === "ACTIVE") {
      return "/monitoring";
    }
    return `/monitoring?monitor_status=${status}`;
  }

  if (matchingStatuses.size > 1) {
    return "/monitoring?monitor_status=ALL";
  }

  return "/monitoring";
}
